import { useCallback, useMemo, useRef, useState } from "react";
import { useAppContext } from "../../../AppContext";
import { useApiMutation } from "../../../common/infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { IAnnouncement } from "../models/announcementModel";
import { normalizeAnnouncements } from "../utilities/announcementContentUtility";

/**
 * A second tap on "Окей, разбрах" this soon after the first is ignored. After the first tap
 * the next announcement slides into the same dialog, under the same button: a double tap
 * would otherwise confirm an announcement nobody has seen.
 */
export const ANNOUNCEMENT_CONFIRM_COOLDOWN_MS = 500;

interface IUseAnnouncementsParams {
  /** False while another dialog (studio picker, phone number...) has the user's attention. */
  enabled?: boolean;
}

/** What the user answered since the app was opened. */
interface IAnnouncementsSession {
  userId: number | undefined;
  /** Announcements confirmed in this session, so they are not shown again while the server catches up. */
  seenIds: number[];
  /** "Покажи ми по-късно": the rest of the queue stays closed until the app is opened again. */
  isLaterChosen: boolean;
}

const startSession = (userId: number | undefined): IAnnouncementsSession => ({
  userId,
  seenIds: [],
  isLaterChosen: false,
});

/**
 * Announcements a global admin published to the user's role: loaded once when the app opens,
 * then shown one at a time, oldest first, until the user confirms each with "Окей, разбрах".
 * "Покажи ми по-късно" closes the whole queue until the next time the app is opened.
 * Nothing is shown (or even requested) in a profile a global admin opened from the admin panel.
 */
export const useAnnouncements = ({ enabled = true }: IUseAnnouncementsParams = {}) => {
  const { isAuthenticated, currentUser } = useAppContext();
  const { mutate: markSeen } = useApiMutation();

  const userId = currentUser?.id;
  // A global admin inside someone's profile must not mark that person's announcements as seen.
  const isImpersonating = !!currentUser?.impersonatedBy;
  const canShow = enabled && !!isAuthenticated && !!currentUser && !isImpersonating;

  const { data, isFetching } = useApiQuery<unknown>(
    {
      // Per user: signing in as someone else without reloading must not reuse the first one's list.
      queryKey: [endpointsMetadata.announcement.getPending, userId],
      endpoint: endpointsMetadata.announcement.getPending,
      globalIsLoading: false,
      enabled: canShow,
      // A failed request just means no announcement this time; it is asked again next open.
      retry: false,
    },
    {
      // Once per app open: never again on focus, reconnect or when a screen mounts.
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  );

  const [session, setSession] = useState<IAnnouncementsSession>(() => startSession(userId));
  // Another user signed in without a reload: they start with a clean slate.
  const activeSession = session.userId === userId ? session : startSession(userId);

  const lastConfirmedAtRef = useRef<number | null>(null);

  const pending = useMemo(() => normalizeAnnouncements(data), [data]);
  const queue = useMemo(
    () => pending.filter((announcement) => !activeSession.seenIds.includes(announcement.id)),
    [pending, activeSession.seenIds],
  );

  const next: IAnnouncement | null = queue[0] ?? null;
  const isVisible = canShow && !activeSession.isLaterChosen && next !== null;
  // Only what is on screen: nothing to confirm while the dialog is closed or not allowed.
  const announcement = isVisible ? next : null;
  const total = pending.length;
  const position = total - queue.length + 1;

  // Other global dialogs (the push-notification invitation) wait while the list is on its way
  // and while an announcement is open, so two dialogs never stack.
  const isLoading = canShow && data === undefined && isFetching;
  const isBlockingOtherDialogs = isVisible || isLoading;

  const onConfirm = useCallback(() => {
    if (!announcement) {
      return;
    }

    const now = Date.now();
    if (
      lastConfirmedAtRef.current !== null &&
      now - lastConfirmedAtRef.current < ANNOUNCEMENT_CONFIRM_COOLDOWN_MS
    ) {
      return;
    }
    lastConfirmedAtRef.current = now;

    // Fire and forget: the next announcement does not wait for the server. If this fails it
    // simply comes back the next time the app is opened.
    markSeen({
      endpoint: endpointsMetadata.announcement.markSeen,
      method: "post",
      queryParams: { id: announcement.id },
      disableSuccessToast: true,
      disableErrorToast: true,
    });

    setSession((previous) => {
      const base = previous.userId === userId ? previous : startSession(userId);
      return { ...base, seenIds: [...base.seenIds, announcement.id] };
    });
  }, [announcement, markSeen, userId]);

  const onLater = useCallback(() => {
    setSession((previous) => {
      const base = previous.userId === userId ? previous : startSession(userId);
      return { ...base, isLaterChosen: true };
    });
  }, [userId]);

  return {
    announcement,
    isVisible,
    /** 1-based place of the shown announcement among those found when the app opened. */
    position,
    total,
    isBlockingOtherDialogs,
    onConfirm,
    onLater,
  };
};
